import * as React from "react";
import { Text } from "@uikit";
import { ChevronLeftIcon, XIcon } from "@heroicons/react/outline";
import { Dialog, Transition } from "@headlessui/react";
import { IoCloseOutline } from "@react-icons/all-files/io5/IoCloseOutline";

interface IProps {
  children: React.ReactNode;
  pinnedMode?: boolean;
  title: string | React.ReactNode;
  onClose?: () => void;
  onBack?: () => void;
  maxWidth?: string;
  fullScreen?: boolean;
  fullScreenMobile?: boolean;
  showBackButton?: boolean;
  hideCloseButton?: boolean;
  visible?: boolean;
}

const Modal = ({
  children,
  onClose,
  onBack,
  title,
  maxWidth = "500px",
  fullScreen = false,
  fullScreenMobile = false,
  pinnedMode = false,
  showBackButton = false,
  hideCloseButton = false,
  visible = false,
}: IProps) => {
  return (
    <Transition.Root show={visible} as={React.Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-[9999] inset-0 ">
          <div className="flex sm:items-center justify-center min-h-full sm:p-4 text-center md:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-100"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`absolute bg-white sm:rounded-lg  shadow-xl transform transition-all   ${
                  pinnedMode
                    ? "bottom-0 left-0 right-0 rounded-t-[6px] sm:bottom-auto sm:left-auto sm:right-auto sm:rounded-lg"
                    : ""
                } ${
                  fullScreen
                    ? "sm:my-8 w-full h-full sm:rounded-none lg:rounded-lg lg:w-[calc(100%-50px)] lg:h-[calc(100%-50px)]"
                    : "sm:my-8 sm:w-full"
                } ${
                  fullScreenMobile
                    ? "sm:my-8 w-full h-full sm:w-auto sm:h-auto"
                    : "sm:my-8 sm:w-full"
                }`}
                style={{
                  maxWidth: maxWidth,
                }}
              >
                <div className="flex flex-col h-full">
                  <div
                    className={`bg-white rounded-[6px] flex flex-col overflow-hidden flex-1 max-h-[calc(100dvh-0px)]`}
                  >
                    <div className="flex relative flex-col border-b border-grey3  px-10 h-[60px] sm:h-[60px] justify-center items-center">
                      {showBackButton && (
                        <div
                          className="absolute left-2 top-0 bottom-0 flex m-auto"
                          onClick={onBack}
                        >
                          <button
                            type="button"
                            className="p-2 rounded-md inline-flex items-center justify-center text-gray-400"
                          >
                            <span className="sr-only">Close menu</span>
                            <ChevronLeftIcon
                              className="h-7 w-7 text-black
                  "
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      )}
                      <Text
                        element="h2"
                        size={19}
                        leading={1.3}
                        weight="semibold"
                        color="black"
                        className="mt-1 text-ellipsis overflow-hidden text-center w-full whitespace-nowrap"
                      >
                        {title}
                      </Text>
                      {!hideCloseButton && (
                        <div
                          className="absolute right-2 md:right-6 lg:right-2 top-0 bottom-0 flex m-auto"
                          onClick={() => {
                            if (onClose) onClose();
                          }}
                        >
                          <button
                            type="button"
                            className="p-2 rounded-md inline-flex items-center justify-center text-gray-400"
                          >
                            <span className="sr-only">Close menu</span>

                            <IoCloseOutline
                              className="h-6 w-6 text-grey7"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className=" flex-1 flex-col w-full sm:w-auto sm:top-auto bottom-0 sm:bottom-auto relative overflow-auto noscrollbar">
                      <div>{children}</div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
